import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, TriangleAlert, X } from 'lucide-react';
import { useTasks } from '../../lib/tasks';

/**
 * What became of the work a dialog started and stopped waiting for.
 *
 * Bottom-right, above everything, and outside every page — the whole point is
 * that the operator has moved on, so the notification has to find them
 * wherever they went.
 *
 * A running task is shown too, quietly. Between confirming and hearing back
 * there would otherwise be no evidence anything is happening, and silence
 * after pressing a button reads as a button that did not work — which is how
 * somebody presses it a second time on the same chassis.
 */
const TONE = {
  running: 'border-line bg-surface',
  ok: 'border-success/30 bg-success-soft',
  failed: 'border-danger/30 bg-danger-soft',
} as const;

export function TaskToasts() {
  const { tasks, dismissToast } = useTasks();
  // Only the bubbles still showing; the tray keeps the rest.
  const toasts = tasks.filter((t) => t.toast);
  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`pointer-events-auto rounded-xl border p-3 shadow-lg ${TONE[t.state]}`}
          role={t.state === 'failed' ? 'alert' : 'status'}
        >
          <div className="flex items-start gap-2">
            <span className="mt-0.5 shrink-0">
              {t.state === 'running' && <Loader2 className="size-4 animate-spin text-muted" />}
              {t.state === 'ok' && <CheckCircle2 className="size-4 text-success" />}
              {t.state === 'failed' && <TriangleAlert className="size-4 text-danger" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">{t.label}</p>
              <p className="mt-0.5 text-xs text-muted">
                {t.state === 'running' ? 'Running — you can keep working.' : t.message}
              </p>
              {t.href && t.state !== 'running' && (
                <Link
                  to={t.href}
                  onClick={() => dismissToast(t.id)}
                  className="mt-1 inline-block text-xs text-link hover:underline"
                >
                  Open it
                </Link>
              )}
            </div>
            {/*
              A running task has no dismiss: hiding it would not stop it, and a
              toast an operator can close while the chassis is still being
              written to is a toast that teaches them the work ended.
            */}
            {t.state !== 'running' && (
              <button
                onClick={() => dismissToast(t.id)}
                className="shrink-0 rounded p-0.5 text-faint hover:text-ink"
                aria-label="Dismiss"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
